import { StateMessage } from '@/components/ui/controls';
import { Card } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import StatTile from '@/components/ui/stat-tile';
import { table } from '@/components/ui/styles';
import type { LeadFacets } from '@/types/api';
import { formatDay } from '@/utils/format';
import { sourceLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { ReportWeeks } from './api-types';
import HBarChart, { type HBarRow } from './hbar-chart';
import ReportsHeader from './reports-nav';
import { AI_SOURCES, SOURCE_SERIES, percent, seriesOf, type ReportSource } from './series';
import StackedColumns from './stacked-columns';
import TableCard from './table-card';

type Week = { key: string; label: string; values: Record<ReportSource, number> };

const total = (w: Week) => SOURCE_SERIES.reduce((n, s) => n + w.values[s.key], 0);

// The share follows the count, so only the count sorts.
const SOURCE_COLUMNS: SortColumns<HBarRow> = {
  source: { value: (r) => r.label },
  leads: { value: (r) => r.value, desc: true },
};

// The weeks stay a time series; the total finds the busiest ones.
const WEEK_COLUMNS: SortColumns<Week> = {
  week: { value: (w) => dateValue(w.key) },
  total: { value: total, desc: true },
};

/**
 * Reports, Sources (ATT-1, ATT-4, ATT-5): where leads first came from, one first touch
 * per lead (GET /leads/facets), and the new leads of the last 12 weeks by that source
 * (GET /reports/sources-weekly). On a phone each table sits under its chart, and the
 * weekly table folds the sources into one line under the week (UI-1).
 */
const SourcesReportPage = () => {
  const facets = useApi<LeadFacets>('/leads/facets');
  const weekly = useApi<ReportWeeks>('/reports/sources-weekly');
  const sources = facets.data?.sources ?? [];
  const leads = sources.reduce((n, f) => n + f.count, 0);
  const known = sources.filter((f) => f.value !== 'unknown'); // sorted by count, largest first
  const unknown = sources.find((f) => f.value === 'unknown')?.count ?? 0;
  const fromAI = sources.filter((f) => AI_SOURCES.has(f.value)).reduce((n, f) => n + f.count, 0);
  const top = known[0];
  const ready = facets.data !== null;

  // Unknown goes last, in grey: it is the absence of a source, not one.
  const rows: HBarRow[] = [
    ...known.map((f) => ({ key: f.value, label: sourceLabel(f.value), value: f.count })),
    ...(unknown ? [{ key: 'unknown', label: 'Unknown', value: unknown, muted: true }] : []),
  ];
  // Every source outside the named series, unknown included, counts as Other.
  const weeks: Week[] = (weekly.data?.weeks ?? []).map((w) => {
    const values: Record<ReportSource, number> = { google_ads: 0, chatgpt: 0, youtube_ads: 0, other: 0 };
    for (const [source, n] of Object.entries(w.counts)) values[seriesOf(source)] += n;
    return { key: w.week, label: formatDay(new Date(`${w.week}T00:00:00Z`)), values };
  });

  const sourceSort = useSort(rows, SOURCE_COLUMNS);
  const weekSort = useSort(weeks, WEEK_COLUMNS);

  return (
    <div>
      <ReportsHeader />
      {facets.error && !ready ? (
        <StateMessage title="Sources could not be loaded" tone="error">
          {facets.error}
        </StateMessage>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            <StatTile label="Leads" value={ready ? String(leads) : '…'} note="Every lead, counted once by first touch" />
            <StatTile label="Source known" value={ready ? percent(leads - unknown, leads) : '…'} note={`${leads - unknown} of ${leads} leads`} />
            <StatTile label="From AI assistants" value={ready ? String(fromAI) : '…'} note="Recognised from the referrer (ATT-4)" />
            <StatTile label="Top source" value={top ? sourceLabel(top.value) : '—'} note={top ? `${top.count} leads, ${percent(top.count, leads)}` : 'No known source yet'} />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]">
            <Card title="Leads by first-touch source">
              {rows.length ? (
                <HBarChart
                  rows={rows}
                  format={String}
                  label="Leads by first-touch source"
                  tooltip={(r) => (
                    <>
                      <span className="font-semibold text-fg-strong">{r.value}</span>
                      <span className="ml-1.5 text-fg-muted">
                        {r.label}, {percent(r.value, leads)}
                      </span>
                    </>
                  )}
                />
              ) : (
                <p className="text-sm text-fg-faint">{ready ? 'No leads yet.' : 'Loading…'}</p>
              )}
              <p className="mt-4 text-xs text-fg-faint">
                First touch: where each lead first came from, kept by a cookie when they come back later (ATT-5). Unknown means nothing was tracked; the
                team asks in Telegram (ATT-2).
              </p>
            </Card>
            <TableCard title="Sources">
              <thead className={table.head}>
                <tr>
                  <SortTh label="Source" column="source" sort={sourceSort.sort} onSort={sourceSort.toggle} />
                  <SortTh label="Leads" column="leads" sort={sourceSort.sort} onSort={sourceSort.toggle} right />
                  <th scope="col" className={`${table.th} text-right`}>Share</th>
                </tr>
              </thead>
              <tbody>
                {sourceSort.rows.map((r) => (
                  <tr key={r.key} className={table.row}>
                    <td className={table.td}>
                      {r.label}
                      {AI_SOURCES.has(r.key) && <span className="ml-2 text-xs text-fg-faint">AI assistant</span>}
                    </td>
                    <td className={`${table.td} text-right tabular-nums`}>{r.value}</td>
                    <td className={`${table.td} text-right tabular-nums text-fg-muted`}>{percent(r.value, leads)}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]">
            <Card title="New leads per week, by first-touch source">
              {weekly.error && !weekly.data ? (
                <p className="text-sm text-danger">The weekly view could not be loaded.</p>
              ) : weeks.length ? (
                <StackedColumns columns={weeks} series={SOURCE_SERIES} label="New leads per week by first-touch source, last 12 weeks" />
              ) : (
                <p className="text-sm text-fg-faint">Loading…</p>
              )}
              <p className="mt-4 text-xs text-fg-faint">The week a lead was added, from Monday. Other includes every other source and unknown.</p>
            </Card>
            <TableCard title="Per week">
              <thead className={table.head}>
                <tr>
                  <SortTh label="Week of" column="week" sort={weekSort.sort} onSort={weekSort.toggle} />
                  {SOURCE_SERIES.map((s) => (
                    <th key={s.key} scope="col" className={`${table.th} hidden text-right md:table-cell`}>
                      {s.label}
                    </th>
                  ))}
                  <SortTh label="Total" column="total" sort={weekSort.sort} onSort={weekSort.toggle} right />
                </tr>
              </thead>
              <tbody>
                {weekSort.rows.map((w) => (
                  <tr key={w.key} className={table.row}>
                    <td className={table.td}>
                      <span className="whitespace-nowrap">{w.label}</span>
                      <span className="mt-0.5 block text-xs text-fg-muted md:hidden">
                        {SOURCE_SERIES.map((s) => `${s.label} ${w.values[s.key]}`).join(' · ')}
                      </span>
                    </td>
                    {SOURCE_SERIES.map((s) => (
                      <td key={s.key} className={`${table.td} hidden text-right tabular-nums md:table-cell`}>
                        {w.values[s.key]}
                      </td>
                    ))}
                    <td className={`${table.td} text-right tabular-nums text-fg-strong`}>{total(w)}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          </div>
        </>
      )}
    </div>
  );
};

export default SourcesReportPage;
